import React, { useState, useEffect } from "react";
import axios from "axios";
import ToDoListContainer from "./_to_do_list_container";
import dayjs from "dayjs";

const APPOINTMENT_RANGE_INCREMENT_DAYS = 15;

const Appointments = () => {
  const [appointments, setAppointments] = useState([]);
  const [location, setLocation] = useState("");
  const [appointmentCount, setAppointmentCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [fromDate, setFromDate] = useState(dayjs());
  const [untilDate, setUntilDate] = useState(dayjs().add(7, "day"));
  const [error, setError] = useState(false);
  const [isAppointmentVisible, setIsAppointmentVisible] = useState(() => {
    const savedState = sessionStorage.getItem("isAppointmentVisible");
    return savedState !== null ? JSON.parse(savedState) : true;
  });

  useEffect(() => {
    if (isAppointmentVisible) {
      loadAppointments(fromDate, untilDate);
    }
  }, [isAppointmentVisible]);

  useEffect(() => {
    // Count total appointments
    const count = Object.values(appointments).reduce(
      (acc, appointmentLists) => acc + appointmentLists.length,
      0
    );
    setAppointmentCount(count);
    // Extract location from the first appointment
    const firstDateKey = Object.keys(appointments)[0];
    if (firstDateKey && appointments[firstDateKey].length > 0) {
      const firstAppointment = appointments[firstDateKey][0];
      if (firstAppointment && firstAppointment.location) {
        setLocation("Location : " + firstAppointment.location);
      } else {
        setLocation("No record found");
      }
    }
  }, [appointments]);

  const loadAppointments = (startDate, endDate) => {
    axios
      .get(
        `/home/load_appointments.json?start_date=${startDate.format(
          "YYYY-MM-DD"
        )}&last_date=${endDate.format("YYYY-MM-DD")}`
      )
      .then((response) => {
        const appointments = response.data;
        setAppointments(appointments);
      })
      .catch((error) => {
        console.error("Error loading appointments:", error);
        setError(true);
      })
      .finally(() => setIsLoading(false));
  };

  const loadMoreAppointments = () => {
    setIsLoading(true);
    const newUntilDate = dayjs(untilDate).add(APPOINTMENT_RANGE_INCREMENT_DAYS, "day");
    setUntilDate(newUntilDate);
    loadAppointments(fromDate, newUntilDate);
  };

  const loadPastAppointments = () => {
    setIsLoading(true);
    const newFromDate = dayjs(fromDate).subtract(APPOINTMENT_RANGE_INCREMENT_DAYS, "day");
    setFromDate(newFromDate);
    loadAppointments(newFromDate, untilDate);
  };

  const toggleAppointmentSection = () => {
    const newState = !isAppointmentVisible;
    setIsAppointmentVisible(newState);
    sessionStorage.setItem("isAppointmentVisible", JSON.stringify(newState));
  };

  let locationMessage = location;
  if (isLoading && !error) locationMessage = "Loading...";
  if (error) locationMessage = "Error: Try again or report it in feedback.";

  return (
    <section
      id="appointment-section"
      className="appointment-container ccw-home-appointments"
      aria-labelledby="appointment-tab"
    >
      {/* -------------------------- Appointment (count) --------------------------*/}
      <div className="tabs-container ccw-home-appointments__header">
        <div className="tabs-row container">
          <button
            id="appointment-tab"
            type="button"
            className="count-container ccw-home-appointments__toggle"
            onClick={toggleAppointmentSection}
            aria-expanded={isAppointmentVisible}
            aria-controls="appointment-content"
          >
            <span className="count" aria-live="polite">
              {isLoading ? "..." : `${appointmentCount}`}
            </span>
            <span>Appointment</span>
            {isAppointmentVisible ? (
              <i className="fa fa-caret-down" aria-hidden="true"></i>
            ) : (
              <i className="fa fa-caret-up" aria-hidden="true"></i>
            )}
          </button>
        </div>
      </div>
      {/* -------------------------- Appointments container --------------------------*/}
      <div
        id="appointment-content"
        className="ccw-home-appointments__panel"
        hidden={!isAppointmentVisible}
      >
        <div className="appointment-todos-container container box ccw-surface">
          {!isLoading && !error && (
            <div className="appointment-top-actions ccw-home-appointments__load-actions">
              <button
                type="button"
                className="ccw-button ccw-button--quiet ccw-home-appointments__load-action"
                onClick={loadPastAppointments}
              >
                {`Show past ${APPOINTMENT_RANGE_INCREMENT_DAYS} days`}
              </button>
            </div>
          )}
          <div className="location">{locationMessage}</div>
          {/* -------------------------- Load appointments --------------------------*/}
          {Object.entries(appointments).map(([date, appointmentLists]) => (
            <section
              key={date}
              className="ccw-home-appointments__date-group"
              aria-labelledby={`appointment-date-${date}`}
            >
              <h3
                id={`appointment-date-${date}`}
                className="shift-banner ccw-home-appointments__date"
              >
                {dayjs(date).format("MMMM DD, YYYY (dddd)")}
              </h3>
              {appointmentLists.map((appointment, idx) => (
                <ToDoListContainer
                  key={idx}
                  toDoList={{ ...appointment, to_do_date: date ? date : appointment.to_do_date }}
                />
              ))}
            </section>
          ))}
          {/* -------------------------- Show more appointments --------------------------*/}
          <div
            id="more-appointments"
            className="more-appointments ccw-home-appointments__load-actions"
          >
            {isLoading && !error ? (
              "Loading..."
            ) : error ? (
              "Error: Try again or report it in feedback."
            ) : (
              <>
                <div>
                  Showing appointments from <b>{fromDate.format("MMMM DD, YYYY")}</b> to{" "}
                  <b>{untilDate.format("MMMM DD, YYYY")}</b>
                </div>
                <button
                  type="button"
                  className="ccw-button ccw-button--quiet ccw-home-appointments__load-action"
                  onClick={loadMoreAppointments}
                >
                  {`Show for ${APPOINTMENT_RANGE_INCREMENT_DAYS} more days`}
                </button>
              </>
            )}
          </div>
          {/* ----------------------------------------------------------------------------*/}
        </div>
      </div>
    </section>
  );
};

export default Appointments;
